import { CharacterLoader } from '@sorrem/engine';
import { createLogger, type BodyPart, type EquipmentPiece } from '@sorrem/shared';
import { useEffect, useState } from 'react';

const logger = createLogger('client:use-character-appearance-catalog');

/**
 * Fetches the body-part catalog (hairstyles, eyebrows, etc.) and the item
 * catalog (names of starting equipment, say) once per mount,
 * via the same `CharacterLoader` the character render worker and debugger
 * already use to talk to `/assets/body-parts` (see `CharacterDebugPage.tsx`,
 * which duplicates this fetch inline rather than through a hook).
 */
export function useCharacterAppearanceCatalog(): { bodyParts: BodyPart[]; equipmentPieces: EquipmentPiece[]; isLoading: boolean } {
    const [bodyParts, setBodyParts] = useState<BodyPart[]>([]);
    const [equipmentPieces, setEquipmentPieces] = useState<EquipmentPiece[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const assetBaseUrl = new URL('/assets/', window.location.origin).href;
        const loader = new CharacterLoader(assetBaseUrl, '/assets');

        void loader.initialize()
            .then(() => {
                if (cancelled) return;
                setBodyParts(loader.getAllBodyParts());
                setEquipmentPieces(loader.getAllEquipmentPieces());
            })
            .catch((error: unknown) => {
                logger.error('Failed to initialize character loader:', error);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return (): void => {
            cancelled = true;
        };
    }, []);

    return { bodyParts, equipmentPieces, isLoading };
}
